/** OrderSyncForm — the embeddable launch half of Order Sync: choose ONE
 *  source mode (updated/created window, products + travel dates, explicit
 *  order IDs, or reconcile-existing — each offered only when the integration's
 *  `order_query` capabilities declare it), set advanced options — then launch
 *  the sync task.
 *
 *  Hosted by OrderSyncPage (which owns the SyncScopePicker and injects it via
 *  `scopeSlot`) and by the tenant setup wizard (fixed scope,
 *  `onSubmit`/`onLaunched` injected so the wizard's preflight item owns the
 *  spawned task instead of navigating away). */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, addMonths, addYears, format } from 'date-fns';
import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { OrderQueryCapabilities } from '@/api/integrations';
import * as opsApi from '@/api/ops';
import type { SyncOrdersStartInput } from '@/api/ops';
import * as syncApi from '@/api/sync';
import { useSnackbar } from '@/components/Snackbar';
import { TimeWindowPicker } from '@/components/TimeWindowPicker';
import { ChipListField } from '@/features/tour-operators/integrations/wizard/ChipListField';

import { useIntegrationCatalog, type SyncScope } from './SyncScopePicker';

/** One selectable option = one external ID belonging to a tour. */
interface TargetOption {
  tourId: number;
  tourName: string;
  value: string;
  kind: 'primary' | 'ext';
  idType?: string;
}

type SourceMode = 'updated' | 'created' | 'products' | 'ids' | 'reconcile';

/** Source modes in display order; each is offered only when the scoped
 *  integration's `order_query` capabilities declare it. */
const SOURCE_MODES: {
  value: SourceMode;
  label: string;
  available: (q: OrderQueryCapabilities) => boolean;
}[] = [
  { value: 'updated', label: 'Updated window', available: (q) => q.updated_range !== null },
  { value: 'created', label: 'Created window', available: (q) => q.created_range !== null },
  {
    value: 'products',
    label: 'Products + travel dates',
    available: (q) => q.by_product_travel_date,
  },
  { value: 'ids', label: 'Order IDs', available: (q) => q.by_ids },
  { value: 'reconcile', label: 'Reconcile existing', available: (q) => q.reconcile_existing },
];

type ReconcileGroup = 'past' | 'active' | 'future';

/** Travel-time groups for reconcile mode (orders with no dated bookings
 *  count as past). Default selection: active + future. */
const RECONCILE_GROUPS: { value: ReconcileGroup; label: string }[] = [
  { value: 'past', label: 'Past — travel ended' },
  { value: 'active', label: 'Active — travel in progress' },
  { value: 'future', label: 'Future — travel not started' },
];

const QUICK_RANGES: { label: string; compute: (from: Date) => Date }[] = [
  { label: '1 day', compute: (d) => addDays(d, 1) },
  { label: '3 days', compute: (d) => addDays(d, 3) },
  { label: '1 week', compute: (d) => addDays(d, 7) },
  { label: '2 weeks', compute: (d) => addDays(d, 14) },
  { label: '1 month', compute: (d) => addMonths(d, 1) },
  { label: '3 months', compute: (d) => addMonths(d, 3) },
  { label: '6 months', compute: (d) => addMonths(d, 6) },
  { label: '1 year', compute: (d) => addYears(d, 1) },
];

/** Above this many order IDs the chip field collapses into a summary. */
const IDS_COLLAPSE_THRESHOLD = 100;

export interface OrderSyncFormProps {
  /** The tenant / integration / account this form queries and syncs for. */
  scope: SyncScope;
  /** Fixed-tenant hosts (the setup wizard) set this; the form itself never
   *  changes the scope, so today it is documentation for hosts. */
  lockScope?: boolean;
  /** The page injects its SyncScopePicker here so the original toolbar row
   *  (picker · targets spinner) survives extraction. */
  scopeSlot?: ReactNode;
  /** Launch override — the setup wizard routes the launch through its
   *  preflight item (stage + execute) instead of /ops/sync_orders/start/. */
  onSubmit?: (input: SyncOrdersStartInput) => Promise<{ id: string }>;
  /** Where to go once launched. Default: navigate to the task's page. */
  onLaunched?: (taskId: string) => void;
}

export function OrderSyncForm({ scope, scopeSlot, onSubmit, onLaunched }: OrderSyncFormProps) {
  const { show } = useSnackbar();
  const navigate = useNavigate();

  const [sourceMode, setSourceMode] = useState<SourceMode>('updated');
  const [updatedFrom, setUpdatedFrom] = useState<Date | null>(null);
  const [updatedTo, setUpdatedTo] = useState<Date | null>(null);
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [selected, setSelected] = useState<TargetOption[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const [reconcileGroups, setReconcileGroups] = useState<ReconcileGroup[]>(['active', 'future']);
  const [dryRun, setDryRun] = useState(false);
  // Advanced options
  const [approval, setApproval] = useState(false);
  const [processingMode, setProcessingMode] = useState<'queue' | 'inline'>('queue');
  const [sendInvitations, setSendInvitations] = useState(false);
  const [sendSurveys, setSendSurveys] = useState(false);

  // A different tenant/integration/account invalidates the tour selection and
  // the update/creation windows — the same resets the page's picker onChange
  // performed before extraction.
  const scopeKey = `${scope.tenant?.id ?? ''}|${scope.integration ?? ''}|${scope.account ?? ''}`;
  useEffect(() => {
    setSelected([]);
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setCreatedFrom(null);
    setCreatedTo(null);
  }, [scopeKey]);

  const scopeReady = Boolean(scope.tenant && scope.integration);

  // Capabilities of the selected integration (SYNC_ORDERS_QUERY).
  const catalog = useIntegrationCatalog();
  const oq = scope.integration ? catalog[scope.integration]?.order_query : undefined;
  // All modes are offered until an integration is scoped (inputs are inert
  // anyway — submit requires a full scope).
  const modes = useMemo(
    () => (oq ? SOURCE_MODES.filter((m) => m.available(oq)) : SOURCE_MODES),
    [oq],
  );
  const effMode: SourceMode = modes.some((m) => m.value === sourceMode)
    ? sourceMode
    : (modes[0]?.value ?? 'updated');

  const targets = useQuery({
    queryKey: ['sync-targets', scope.tenant?.id, scope.integration, scope.account],
    queryFn: () =>
      syncApi.fetchSyncTargets({
        tour_operator_id: scope.tenant!.id,
        integration: scope.integration!,
        account: scope.account,
      }),
    enabled: scopeReady && oq?.by_product_travel_date === true,
  });

  const options = useMemo<TargetOption[]>(() => {
    const out: TargetOption[] = [];
    for (const t of targets.data?.results ?? []) {
      if (t.primary) {
        out.push({ tourId: t.tour.id, tourName: t.tour.name, value: t.primary, kind: 'primary' });
      }
      for (const e of t.ext_ids) {
        out.push({
          tourId: t.tour.id,
          tourName: t.tour.name,
          value: e.value,
          kind: 'ext',
          idType: e.id_type,
        });
      }
    }
    return out;
  }, [targets.data]);

  const startMut = useMutation({
    mutationFn: () =>
      (onSubmit ?? opsApi.startSyncOrders)({
        tour_operator_id: scope.tenant!.id,
        integration_name: scope.integration!,
        account: scope.account,
        ...(effMode === 'updated' && updatedFrom
          ? {
              updated_from: updatedFrom.toISOString(),
              ...(updatedTo ? { updated_to: updatedTo.toISOString() } : {}),
            }
          : {}),
        ...(effMode === 'created' && createdFrom
          ? {
              created_from: createdFrom.toISOString(),
              ...(createdTo ? { created_to: createdTo.toISOString() } : {}),
            }
          : {}),
        ...(effMode === 'products' && fromDate && toDate
          ? {
              product_ids: selected.map((o) => o.value),
              from_date: format(fromDate, 'yyyy-MM-dd'),
              to_date: format(toDate, 'yyyy-MM-dd'),
            }
          : {}),
        ...(effMode === 'ids' ? { order_ids: orderIds } : {}),
        ...(effMode === 'reconcile'
          ? {
              reconcile_existing: true,
              ...(oq?.reconcile_groups ? { reconcile_groups: reconcileGroups } : {}),
            }
          : {}),
        dry_run: dryRun,
        approval_required: !dryRun && approval,
        processing_mode: processingMode,
        send_invitations: sendInvitations,
        send_surveys: sendSurveys,
      }),
    onSuccess: (r) => {
      show(dryRun ? 'Dry run (fetch only) started.' : 'Order sync started.', 'success');
      if (onLaunched) onLaunched(r.id);
      else navigate(`/bgtasks/${r.id}`);
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  /** Group toggle: select/deselect every ID of a tour. */
  const toggleTour = (tourId: number) => {
    const tourOptions = options.filter((o) => o.tourId === tourId);
    const allIn = tourOptions.every((o) => selected.some((s) => s.value === o.value));
    setSelected((prev) =>
      allIn
        ? prev.filter((s) => s.tourId !== tourId)
        : [...prev.filter((s) => s.tourId !== tourId), ...tourOptions],
    );
  };

  /** Merge a text file (one order ID per line) into the chips, deduped. */
  const handleIdsFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result ?? '')
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (!lines.length) {
        show('The file contains no order IDs.', 'warning');
        return;
      }
      setOrderIds((prev) => {
        const merged = Array.from(new Set([...prev, ...lines]));
        show(`Loaded ${merged.length - prev.length} new order ID(s) from ${file.name}.`, 'success');
        return merged;
      });
    };
    reader.onerror = () => show(`Could not read ${file.name}.`, 'error');
    reader.readAsText(file);
  };

  const sourceOk =
    effMode === 'updated'
      ? updatedFrom !== null
      : effMode === 'created'
        ? createdFrom !== null
        : effMode === 'products'
          ? selected.length > 0 && Boolean(fromDate && toDate)
          : effMode === 'ids'
            ? orderIds.length > 0
            : !oq?.reconcile_groups || reconcileGroups.length > 0; // reconcile: ≥1 group

  const toggleReconcileGroup = (value: ReconcileGroup) =>
    setReconcileGroups((prev) =>
      prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value],
    );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
          {scopeSlot}
          {targets.isFetching && <CircularProgress size={20} />}
        </Box>

        <Stack spacing={2} sx={{ maxWidth: 900 }}>
          <Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={effMode}
              onChange={(_, v) => v && setSourceMode(v as SourceMode)}
            >
              {modes.map((m) => (
                <ToggleButton key={m.value} value={m.value}>
                  {m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {effMode === 'updated' && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                minHeight: 40,
              }}
            >
              <TimeWindowPicker
                keepRelative={false}
                label="Updated"
                allowOpenEnded
                value={[updatedFrom, updatedTo]}
                onChange={([from, to]) => {
                  setUpdatedFrom(from);
                  setUpdatedTo(to);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Start required — empty &quot;to&quot; = up to now
              </Typography>
            </Box>
          )}

          {effMode === 'created' && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                minHeight: 40,
              }}
            >
              <TimeWindowPicker
                keepRelative={false}
                label="Created"
                allowOpenEnded
                value={[createdFrom, createdTo]}
                onChange={([from, to]) => {
                  setCreatedFrom(from);
                  setCreatedTo(to);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Start required — empty &quot;to&quot; = up to now
              </Typography>
            </Box>
          )}

          {effMode === 'products' && (
            <>
              <Autocomplete<TargetOption, true>
                multiple
                options={options}
                value={selected}
                onChange={(_, v) => setSelected(v)}
                disabled={!scopeReady || targets.isLoading}
                groupBy={(o) => `${o.tourName} [#${o.tourId}]`}
                getOptionLabel={(o) => o.value}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(
                    (o) =>
                      o.tourName.toLowerCase().includes(q) ||
                      String(o.tourId) === q ||
                      o.value.toLowerCase().includes(q),
                  );
                }}
                renderGroup={(params) => {
                  const groupOptions = options.filter(
                    (o) => `${o.tourName} [#${o.tourId}]` === params.group,
                  );
                  const tourId = groupOptions[0]?.tourId;
                  const inSelection = groupOptions.filter((o) =>
                    selected.some((s) => s.value === o.value),
                  ).length;
                  return (
                    <li key={params.key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          py: 0.25,
                          position: 'sticky',
                          top: -8,
                          bgcolor: 'background.paper',
                          zIndex: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                          cursor: 'pointer',
                        }}
                        onClick={() => tourId !== undefined && toggleTour(tourId)}
                      >
                        <Checkbox
                          size="small"
                          checked={inSelection > 0 && inSelection === groupOptions.length}
                          indeterminate={inSelection > 0 && inSelection < groupOptions.length}
                          sx={{ py: 0.5 }}
                        />
                        <Typography variant="subtitle2">{params.group}</Typography>
                      </Box>
                      <Box component="ul" sx={{ p: 0 }}>
                        {params.children}
                      </Box>
                    </li>
                  );
                }}
                renderOption={(props, o) => (
                  <Box component="li" {...props} key={`${o.tourId}-${o.value}`} sx={{ pl: 4 }}>
                    <Checkbox
                      size="small"
                      checked={selected.some((s) => s.value === o.value)}
                      sx={{ py: 0, mr: 1 }}
                    />
                    <code>{o.value}</code>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={o.kind === 'primary' ? 'primary' : o.idType}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                )}
                renderTags={(vals, getTagProps) =>
                  vals.map((o, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${o.tourId}-${o.value}`}
                      size="small"
                      label={`${o.value} (${o.tourName})`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tours / external IDs"
                    placeholder={
                      scopeReady
                        ? 'Search by tour name, tour ID or external ID'
                        : 'Choose tenant + integration first'
                    }
                    helperText={
                      scopeReady && !targets.isLoading
                        ? `${options.length} selectable IDs across ${targets.data?.results.length ?? 0} tours — select at least one`
                        : undefined
                    }
                  />
                )}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                <DatePicker
                  label="Travel from"
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 170 } },
                  }}
                />
                <DatePicker
                  label="Travel to"
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 170 },
                      helperText: 'Both dates required',
                    },
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ alignItems: 'center', minHeight: 40 }}
                >
                  {QUICK_RANGES.map((r) => (
                    <Chip
                      key={r.label}
                      size="small"
                      label={r.label}
                      variant="outlined"
                      onClick={() => fromDate && setToDate(r.compute(fromDate))}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {effMode === 'ids' && (
            <Stack spacing={1.5}>
              {orderIds.length > IDS_COLLAPSE_THRESHOLD ? (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={() => setOrderIds([])}>
                      Clear
                    </Button>
                  }
                >
                  {orderIds.length} order IDs loaded.
                </Alert>
              ) : (
                <ChipListField
                  label="Order IDs"
                  values={orderIds}
                  onChange={setOrderIds}
                  placeholder="Type an ID and press Enter"
                  helperText="Each ID becomes a badge. Windows and product filters are not used in this mode."
                />
              )}
              <Box>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                >
                  Upload file (one ID per line)
                  <input
                    hidden
                    type="file"
                    accept=".txt,.csv,text/plain"
                    onChange={handleIdsFile}
                  />
                </Button>
              </Box>
            </Stack>
          )}

          {effMode === 'reconcile' && (
            <Stack spacing={1}>
              <Alert severity="info">
                Refetches orders already stored locally for this integration/account — use to repair
                stale order data.
              </Alert>
              {oq?.reconcile_groups && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Order groups (by travel time; orders with no dated bookings count as past)
                  </Typography>
                  <FormGroup row>
                    {RECONCILE_GROUPS.map((g) => (
                      <FormControlLabel
                        key={g.value}
                        control={
                          <Checkbox
                            checked={reconcileGroups.includes(g.value)}
                            onChange={() => toggleReconcileGroup(g.value)}
                          />
                        }
                        label={g.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Stack>
          )}

          <FormControlLabel
            control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
            label="Dry run — fetch orders + build artifacts, do not process"
          />

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Advanced options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={processingMode}
                    onChange={(_, v) => v && setProcessingMode(v as 'queue' | 'inline')}
                  >
                    <ToggleButton value="queue">Queue</ToggleButton>
                    <ToggleButton value="inline">Inline</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Queue (default) replays each order via the per-order webhook queue, serialized
                    with live webhooks. Inline processes in the task pod with exact per-order
                    results — small race risk with concurrent webhooks.
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={approval && !dryRun}
                          disabled={dryRun}
                          onChange={(e) => setApproval(e.target.checked)}
                        />
                      }
                      label="Review before processing (approval step)"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={sendInvitations}
                          onChange={(e) => setSendInvitations(e.target.checked)}
                        />
                      }
                      label="Send invitations"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={sendSurveys}
                          onChange={(e) => setSendSurveys(e.target.checked)}
                        />
                      }
                      label="Send surveys"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Backfills suppress guest-facing notifications by default — both send switches
                    start off.
                  </Typography>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Box>
            <Button
              variant="contained"
              size="large"
              color={dryRun ? 'secondary' : 'primary'}
              disabled={!scopeReady || !sourceOk || startMut.isPending}
              onClick={() => startMut.mutate()}
            >
              {dryRun ? 'Start dry run' : 'Start order sync'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
